import { CSSProperties, KeyboardEvent, MouseEvent, ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'
import cx from 'classnames'
import { FocusScope } from '@react-aria/focus'

import { ReactComponent as CloseIcon } from 'assets/icons/close24.svg'
import Styles from './LegacyAntdModal.module.scss'

/**
 * Legacy modal kept for call sites that have not yet migrated to almanac-ui/Modal.
 * Prop surface: `visible`, `onCancel`, `title`, `footer`, `closable`, `closeIcon`,
 * `wrapClassName`, `width`, `bodyStyle`, `centered`. New code should use
 * almanac-ui/Modal instead.
 */
export interface LegacyAntdModalProps {
  visible: boolean
  onCancel?: () => void
  children?: ReactNode
  title?: ReactNode
  footer?: ReactNode
  closable?: boolean
  closeIcon?: ReactNode
  wrapClassName?: string
  width?: number | string
  bodyStyle?: CSSProperties
  centered?: boolean
  maskClosable?: boolean
  mask?: boolean
  zIndex?: number
}

export const LegacyAntdModal = ({
  visible,
  onCancel,
  children,
  title,
  footer,
  closable = true,
  closeIcon,
  wrapClassName,
  width,
  bodyStyle,
  centered = false,
  maskClosable = true,
  mask = true,
  zIndex,
}: LegacyAntdModalProps) => {
  useEffect(() => {
    if (!visible) return
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onCancel?.()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [visible, onCancel])

  if (!visible) return null

  const handleUnderlayClick = () => {
    if (maskClosable) onCancel?.()
  }

  const handleModalClick = (e: MouseEvent) => {
    e.stopPropagation()
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation()
      onCancel?.()
    }
  }

  let widthStyle: CSSProperties | undefined
  if (width !== undefined) {
    widthStyle = typeof width === 'number' ? { width: `${width}px` } : { width }
  }

  return createPortal(
    <div
      className={cx(Styles.wrapper, wrapClassName, { [Styles.notCentered]: !centered })}
      style={zIndex !== undefined ? { zIndex } : undefined}
    >
      {mask && <div className={Styles.underlay} onClick={handleUnderlayClick} />}
      <FocusScope contain restoreFocus autoFocus>
        <div
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          className={Styles.modal}
          style={widthStyle}
          onClick={handleModalClick}
          onKeyDown={handleKeyDown}
        >
          {(title || closable) && (
            <div className={Styles.header}>
              {title ? <h2 className={Styles.headerTitle}>{title}</h2> : <span />}
              {closable && (
                <button type="button" className={Styles.closeButton} onClick={onCancel} aria-label="Close">
                  {closeIcon || <CloseIcon />}
                </button>
              )}
            </div>
          )}
          <div className={Styles.body} style={bodyStyle}>
            {children}
          </div>
          {footer && <div className={Styles.footer}>{footer}</div>}
        </div>
      </FocusScope>
    </div>,
    document.body,
  )
}
